import { useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { COUNTRY } from 'src/app/shared/constants/configObjectProperties';
import {
  HIRER_AD_PURCHASER_FIRST_TIME,
  HIRER_AD_PURCHASER_REPEAT,
} from 'src/app/shared/constants/tealium';
import { getConfig } from 'src/app/utils/config';
import { log } from 'src/app/utils/logging';
import { actionUpdateIsConfidential } from 'src/app/v1/state/modules/formData/isConfidential';
import { actionUpdateAdvertiserName } from 'src/app/v1/state/modules/session/advertiserName';
import { actionUpdateBillToAgent } from 'src/app/v1/state/modules/session/billToAgent';
import { actionUpdateBillingAccountId } from 'src/app/v1/state/modules/session/billingAccountId';
import { actionUpdateHirerAccount } from 'src/app/v1/state/modules/session/hirerAccount';
import { actionUpdateHirerIsNew } from 'src/app/v1/state/modules/session/hirerIsNew';
import { actionUpdatePhoneNumber } from 'src/app/v1/state/modules/session/phoneNumber';
import { actionUpdatePostalAddress } from 'src/app/v1/state/modules/session/postalAddress';
import { actionUpdateCountry } from 'src/app/v1/state/modules/tealium/country';
import { actionUpdateHirerAdPurchaser } from 'src/app/v1/state/modules/tealium/hirerAdPurchaser';
import {
  actionUpdateIsLinkOutApplyViaSeek,
  actionResetIsLinkOutApplyViaSeek,
} from 'src/app/v1/state/modules/tealium/isLinkOutApplyViaSeek';
import {
  actionUpdateIsOnCreditHold,
  actionResetIsOnCreditHold,
} from 'src/app/v1/state/modules/tealium/isOnCreditHold';
import { actionUpdateIsPrivateAd } from 'src/app/v1/state/modules/tealium/isPrivateAd';
import { actionUpdatePriceVisibility } from 'src/app/v1/state/modules/tealium/priceVisibility';
import {
  BillingStatus,
  type PhysicalAddress,
  useGetHirerAccountQuery,
} from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';
import type { HirerAccount } from 'src/types/hirerAccount';

export const useHirerAccount = () => {
  const {
    isDataCleanupComplete,
    linkOutApplyVia,
    seekAdvertiserId,
    adCentreAdvertiserId,
  } = useSelector(
    (state: AppState) => ({
      isDataCleanupComplete: state.app.isDataCleanupComplete,
      linkOutApplyVia: state.formData.linkOutApplyVia,
      seekAdvertiserId: state.session.seekAdvertiserId,
      adCentreAdvertiserId: state.session.advertiserId,
    }),
    shallowEqual,
  );
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();

  useGetHirerAccountQuery({
    variables: { advertiserId: Number(seekAdvertiserId), adCentreAdvertiserId },
    skip: !isDataCleanupComplete || !seekAdvertiserId || !adCentreAdvertiserId,
    onCompleted: (data) => {
      if (data?.hirerAccount) {
        const hirerAccountData = data?.hirerAccount as HirerAccount;

        // Workaround in case delay adding new accounts to hirer-accounts-api
        // can be removed when hirer-accounts-api becomes source of truth in April 2022
        if (!Boolean(data?.hirerAccount?.nation)) {
          const urlCountryCode = getConfig(COUNTRY);
          hirerAccountData.nation = urlCountryCode;
        }

        if (Boolean(data?.hirerAccount?.isPrivateAdvertiser)) {
          dispatch(actionUpdateIsConfidential(true));
          dispatch(actionUpdateIsPrivateAd(true));
        }

        dispatch(actionUpdateCountry(hirerAccountData.nation.toLowerCase()));
        dispatch(actionUpdateHirerAccount(hirerAccountData));
        dispatch(
          actionUpdatePriceVisibility(
            hirerAccountData.hidePrice ? 'hidden' : 'shown',
          ),
        );

        // Update billing account id and address
        if (Boolean(data?.hirerAccountDetails?.billingAccountId)) {
          dispatch(
            actionUpdateBillingAccountId(
              data?.hirerAccountDetails?.billingAccountId as string,
            ),
          );
        }

        if (Boolean(data?.hirerAccountDetails?.businessName)) {
          dispatch(
            actionUpdateAdvertiserName(
              data?.hirerAccountDetails?.businessName as string,
            ),
          );
        }

        if (Boolean(data?.hirerAccountDetails?.postalAddress)) {
          dispatch(
            actionUpdatePostalAddress(
              data?.hirerAccountDetails?.postalAddress as PhysicalAddress,
            ),
          );
        }

        if (data?.hirerAccount?.hasLinkOutEnabled) {
          dispatch(actionUpdateIsLinkOutApplyViaSeek(linkOutApplyVia));
        } else {
          dispatch(actionResetIsLinkOutApplyViaSeek());
        }

        if (
          data?.hirerAccount?.billingStatus === BillingStatus.ON_CREDIT_HOLD
        ) {
          dispatch(actionUpdateIsOnCreditHold(true));
        } else {
          dispatch(actionResetIsOnCreditHold());
        }

        if (
          data?.hirerAccount?.hasAgent &&
          data?.hirerAccount?.billingStatus === BillingStatus.OK
        ) {
          dispatch(actionUpdateBillToAgent(true));
        }
        const isFirstTimeAdPoster = data?.hirerAccount?.isApproved === false;
        const value = isFirstTimeAdPoster
          ? HIRER_AD_PURCHASER_FIRST_TIME
          : HIRER_AD_PURCHASER_REPEAT;
        dispatch(actionUpdateHirerAdPurchaser(value));
        dispatch(actionUpdateHirerIsNew(isFirstTimeAdPoster));
      }
      if (Boolean(data?.hirerAccountDetails?.primaryContact?.phoneNumber)) {
        dispatch(
          actionUpdatePhoneNumber(
            data?.hirerAccountDetails?.primaryContact?.phoneNumber as string,
          ),
        );
      }
      setIsLoading(false);
    },
    onError: (error) => {
      log.error('HirerAccount | Getting hirer account from graph failed', {
        error,
        isDataCleanupComplete,
        linkOutApplyVia,
        seekAdvertiserId,
      });
      setIsLoading(false);
    },
  });

  return { isLoading };
};
